import type { NotificationStackPosition } from 'Modules/notification/NotificationStack/index.types';
import { useNotificationStore } from 'Modules/notification/store';
import { usePersonStore } from 'Modules/person/index.store';
import { ReportIssueIframe } from 'Modules/reportIssue/components/ReportIssueIframe';
import {
  REPORT_ISSUE_ERROR_MESSAGE,
  REPORT_ISSUE_ERROR_TITLE,
  REPORT_ISSUE_MODAL_TESTID,
  REPORT_ISSUE_MODAL_TITLE,
  REPORT_ISSUE_SUBJECT,
} from 'Modules/reportIssue/index.constants';
import {
  buildReportIssueUrl,
  mapPersonToReportIssuePrefill,
} from 'Modules/reportIssue/utils/buildReportIssueUrl';
import { checkFormReachable } from 'Modules/reportIssue/utils/checkFormReachable';
import { useConfirmationModalStore } from 'Modules/ui/modals/store';
import React from 'react';

const NOTIFICATION_POSITION: NotificationStackPosition = 'tr';

const isReportIssueModalOpen = (): boolean => {
  const { isOpen, testid } = useConfirmationModalStore.getState();
  return isOpen && testid === REPORT_ISSUE_MODAL_TESTID;
};

export type UseReportIssueOptions = {
  /** Value prefilled in the form's "Subject" field. */
  subject?: string;
  /**
   * When the browser reports no internet connection, skip the reachability
   * check and open the modal right away, with an error notification whose
   * Retry reloads the form inside the modal.
   */
  openWhenOffline?: boolean;
};

/**
 * Provides a handler to open the report-issue modal.
 *
 * Checks whether the report-issue form host is reachable before opening the
 * modal. Shows an error notification with a retry action when the form host
 * is unavailable.
 *
 * With `openWhenOffline`, the check is skipped while the browser is offline:
 * the modal opens immediately alongside a persistent error notification.
 * Retry reloads the embedded form in place, reopening the modal if the user
 * closed it. Dismissing the notification closes the modal; closing the modal
 * keeps the notification.
 *
 * Prefill data is obtained from the current person store state on every open.
 * Closing the modal without submitting does not persist any form data.
 */
export const useReportIssue = ({
  subject = REPORT_ISSUE_SUBJECT,
  openWhenOffline = false,
}: UseReportIssueOptions = {}) => {
  const { setOpenConfirmationModal } = useConfirmationModalStore();
  const openReportIssueModalRef = React.useRef<() => void>(() => { });
  const isCheckingRef = React.useRef(false);
  const iframeLoadCountRef = React.useRef(0);

  const removeOfflineNotificationRef = React.useRef<() => void>(() => { });

  /**
   * Opens the modal, or replaces the iframe of the already open modal so the
   * form reloads with fresh prefill data without closing the dialog.
   */
  const showReportIssueModal = React.useCallback(() => {
    const prefill = mapPersonToReportIssuePrefill(usePersonStore.getState());
    const src = buildReportIssueUrl(prefill, subject);
    iframeLoadCountRef.current += 1;

    setOpenConfirmationModal({
      title: REPORT_ISSUE_MODAL_TITLE,
      testid: REPORT_ISSUE_MODAL_TESTID,
      size: 'embed',
      description: (
        <ReportIssueIframe key={iframeLoadCountRef.current} src={src} />
      ),
    });
  }, [setOpenConfirmationModal, subject]);

  const openOfflineReportIssueModal = React.useCallback(() => {
    removeOfflineNotificationRef.current();

    let notificationId: string | number | null = null;
    // removeNotify invokes onRemove synchronously, so this distinguishes our
    // own removals from the user dismissing the notification.
    let isRemovingProgrammatically = false;

    const removeNotification = () => {
      if (notificationId === null) return;

      isRemovingProgrammatically = true;
      useNotificationStore
        .getState()
        .removeNotify(NOTIFICATION_POSITION, notificationId);
      isRemovingProgrammatically = false;
      notificationId = null;
    };

    const notify = () => {
      notificationId = useNotificationStore.getState().addNotify({
        type: 'error',
        position: NOTIFICATION_POSITION,
        autoDismiss: false,
        dismissible: true,
        title: REPORT_ISSUE_ERROR_TITLE,
        message: REPORT_ISSUE_ERROR_MESSAGE,
        action: {
          label: 'Retry',
          onClick: () => void retry(),
        },
        onRemove: () => {
          if (isRemovingProgrammatically) return;

          notificationId = null;
          if (isReportIssueModalOpen()) {
            useConfirmationModalStore.getState().close();
          }
        },
      });
    };

    const retry = async () => {
      removeNotification();

      const isReachable = navigator.onLine && (await checkFormReachable());

      if (!isReachable) {
        if (!isReportIssueModalOpen()) {
          showReportIssueModal();
        }
        notify();
        return;
      }

      showReportIssueModal();
    };

    removeOfflineNotificationRef.current = removeNotification;
    showReportIssueModal();
    notify();
  }, [showReportIssueModal]);

  const openReportIssueModal = React.useCallback(async () => {
    if (openWhenOffline && !navigator.onLine) {
      openOfflineReportIssueModal();
      return;
    }

    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    const retry = () => void openReportIssueModalRef.current();

    const isReachable = await checkFormReachable();
    isCheckingRef.current = false;

    if (!isReachable) {
      useNotificationStore.getState().addNotify({
        type: 'error',
        autoDismiss: true,
        dismissible: true,
        title: REPORT_ISSUE_ERROR_TITLE,
        message: REPORT_ISSUE_ERROR_MESSAGE,
        action: {
          label: 'Retry',
          onClick: retry,
        },
      });
      return;
    }

    showReportIssueModal();
  }, [openWhenOffline, openOfflineReportIssueModal, showReportIssueModal]);

  const openReportIssueModalStable = React.useCallback(() => {
    void openReportIssueModal();
  }, [openReportIssueModal]);

  openReportIssueModalRef.current = openReportIssueModalStable;

  return { openReportIssueModal: openReportIssueModalStable };
};
